import { useEffect } from "react";
import type { ApiClient } from "../api/client";
import { useCartStore } from "../store/cart-store";
import { getSessionCookie } from "../lib/session-cookie";
import { sendTrackingEvent } from "../lib/tracking";
import { CartItemList } from "./Cart/CartItemList";
import { CartCheckoutForm } from "./Cart/CartCheckoutForm";
import { CartSuccessScreen } from "./Cart/CartSuccessScreen";
import { CartBreadcrumb } from "./Cart/Breadcrumb";
import { useTranslations } from "../i18n";

type CartWidgetProps = {
  apiClient: ApiClient;
  onSuccess?: (cartId: string, cartNumber: string) => void;
  onError?: (error: Error) => void;
};

export function CartWidget({
  apiClient,
  onSuccess,
  onError,
}: CartWidgetProps) {
  const { cart, isLoading, error, setCart, setLoading, setError, currentStep } =
    useCartStore();
  const { t } = useTranslations();

  // Charger le panier via le session token du cookie
  useEffect(() => {
    const sessionToken = getSessionCookie();

    if (!sessionToken) {
      setLoading(false);
      // Pas de session = panier vide, on affiche l'état vide
      return;
    }

    setLoading(true);
    apiClient
      .getCartBySession(sessionToken)
      .then((response) => {
        if (response.success && response.cart) {
          setCart(response.cart);

          // Tracking view_cart
          const activeBookings = response.cart.bookings.filter(
            (b: { status: string }) => b.status === "IN_CART",
          );
          if (activeBookings.length > 0) {
            sendTrackingEvent("view_cart", null, {
              currency: "EUR",
              value: response.cart.totalAmount / 100,
              items: activeBookings.map((item: { id: string; productTitle: string; optionName: string; eventDate: string | null; itemsTotal: number }) => ({
                item_id: item.id,
                item_name: item.productTitle,
                item_category: item.optionName,
                item_variant: item.eventDate ?? "",
                price: item.itemsTotal / 100,
                quantity: 1,
              })),
            });
          }
        } else {
          // Pas de panier actif pour cette session
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error loading cart:", err);
        setError(
          err instanceof Error
            ? err.message
            : t.cart.errorLoadingCart,
        );
        onError?.(
          err instanceof Error ? err : new Error(t.common.errorLoading),
        );
      });
  }, [apiClient, setCart, setLoading, setError, onError]);

  const cartId = cart?.id || "";

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 gap-4">
        {/* Loader moderne avec 3 points animés */}
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full bg-brand-500 animate-bounce"
            style={{ animationDelay: "0ms", animationDuration: "600ms" }}
          />
          <span
            className="w-3 h-3 rounded-full bg-brand-500 animate-bounce"
            style={{ animationDelay: "150ms", animationDuration: "600ms" }}
          />
          <span
            className="w-3 h-3 rounded-full bg-brand-500 animate-bounce"
            style={{ animationDelay: "300ms", animationDuration: "600ms" }}
          />
        </div>
        <span className="text-sm text-gray-500 font-medium">
          {t.cart.loadingCart}
        </span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-4 sm:p-6 border border-red-200">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-red-100">
            <svg
              className="size-5 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-red-900">{t.common.error}</h3>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart (no session or no active cart)
  if (!cart || cart.bookings.length === 0) {
    return <CartItemList apiClient={apiClient} cartId={cartId} />;
  }

  return (
    <div className="flex flex-col gap-4 animate-in fade-in duration-300">
      {/* Fil d'Ariane */}
      <CartBreadcrumb />

      {/* Étape panier */}
      {currentStep === "cart" && (
        <CartItemList apiClient={apiClient} cartId={cartId} />
      )}

      {/* Étapes info + paiement */}
      {(currentStep === "info" || currentStep === "payment") && (
        <CartCheckoutForm
          apiClient={apiClient}
          cartId={cartId}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}

      {/* Étape succès */}
      {currentStep === "success" && (
        <CartSuccessScreen apiUrl={apiClient.getBaseUrl()} />
      )}
    </div>
  );
}
